import { loginSession, refreshSession, ensureAccessSession, installSessionRecovery } from './sessionProtocol';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  User,
  LoginRequest,
  SignupRequest,
  AuthResponse,
} from '../types/index';
import type { MesttiOpsAccess, MyMesttiOpsAccess, UserAccess } from '../../../types/access';
import {
  resolveKeepAliveRequestPanePath,
  shouldAllowKeepAliveHttpRequest,
} from '../routing/keepAliveNetworkGate';
import {
  createKeepAliveInactivePaneError,
  isKeepAliveInactivePaneError,
} from '../routing/keepAliveErrors';
import { attachRateLimitInterceptor } from './rateLimit';

export interface KeepAliveAxiosRequestConfig {
  keepAlivePanePath?: string;
}

type FavoriteTabResponseItem = {
  id: string;
  user_id: string;
  company_id: string;
  path: string;
  label: string;
  order_index: number;
  created_at: string;
  updated_at: string;
};

class ApiService {
  private api: AxiosInstance;

  constructor() {
    // Importação dinâmica para evitar problemas de ordem de inicialização
    const apiConfig = require('../../../config/api');
    this.api = axios.create({
      baseURL: apiConfig.API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para adicionar token de autenticação
    this.api.interceptors.request.use(async (config) => {
      const panePath = resolveKeepAliveRequestPanePath(
        (config as KeepAliveAxiosRequestConfig).keepAlivePanePath,
      );

      if (!shouldAllowKeepAliveHttpRequest(panePath)) {
        return Promise.reject(createKeepAliveInactivePaneError());
      }

      const token = await ensureAccessSession();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Ver httpService: 429 é interpretado, não repetido.
    attachRateLimitInterceptor(this.api);
    installSessionRecovery(this.api);

    // Interceptor para tratar erros de autenticação
    this.api.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (isKeepAliveInactivePaneError(error)) {
          return Promise.reject(error);
        }

        console.warn('Erro na API:', error.response?.status);

        // Session lifecycle belongs exclusively to installSessionRecovery.


        return Promise.reject(error);
      }
    );
  }

  // Autenticação
  async login(data: LoginRequest): Promise<AuthResponse> {
    return loginSession(data.email, data.password);
  }
  async refreshToken(): Promise<any> { return refreshSession(); }

  async signup(data: SignupRequest): Promise<User> {
    const response: AxiosResponse<User> = await this.api.post('/register', data);
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<{ status: string }> {
    const response: AxiosResponse<{ status: string }> = await this.api.get('/health');
    return response.data;
  }

  // Usuários
  async getUsers(skip: number = 0, limit: number = 100): Promise<{ users: User[], total: number, page: number, per_page: number, total_pages: number }> {
    try {
      const params = new URLSearchParams();
      params.append('skip', skip.toString());
      params.append('limit', limit.toString());
      
      const response: AxiosResponse<{ users: User[], total: number, page: number, per_page: number, total_pages: number }> = await this.api.get(`/users?${params}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      throw error;
    }
  }

  async getUser(id: string): Promise<User> {
    try {
      const response: AxiosResponse<User> = await this.api.get(`/users/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar usuário:', error);
      throw error;
    }
  }

  async createUser(data: { email: string, full_name: string, password: string, user_level: string, is_active: boolean }): Promise<User> {
    try {
      const response: AxiosResponse<User> = await this.api.post('/users', data);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      throw error;
    }
  }

  async updateUser(id: string, data: Partial<{ email: string, full_name: string, password: string, user_level: string, is_active: boolean }>): Promise<User> {
    try {
      const response: AxiosResponse<User> = await this.api.put(`/users/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      throw error;
    }
  }

  async deleteUser(id: string): Promise<void> {
    try {
      await this.api.delete(`/users/${id}`);
    } catch (error) {
      console.error('Erro ao deletar usuário:', error);
      throw error;
    }
  }

  // Controle de Acesso
  async getUserAccesses(userId: string): Promise<UserAccess[]> {
    try {
      // console.log('[FRONTEND] ApiService - getUserAccesses called for userId:', userId);
      // console.log('[FRONTEND] ApiService - Making request to:', `/users/${userId}/accesses`);
      const response: AxiosResponse<UserAccess[]> = await this.api.get(`/users/${userId}/accesses`);
      // console.log('[FRONTEND] ApiService - getUserAccesses response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[FRONTEND] ApiService - Erro ao buscar acessos do usuário:', error);
      console.error('[FRONTEND] ApiService - Error details:', error.response?.data);
      throw error;
    }
  }

  async createUserAccess(userId: string, accessData: { access_type: string, is_active: boolean }): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post(`/users/${userId}/accesses`, accessData);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar acesso do usuário:', error);
      throw error;
    }
  }

  async updateUserAccess(accessId: string, accessData: Partial<{ access_type: string, is_active: boolean }>): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.put(`/users/accesses/${accessId}`, accessData);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar acesso do usuário:', error);
      throw error;
    }
  }

  async deleteUserAccess(accessId: string): Promise<void> {
    try {
      await this.api.delete(`/users/accesses/${accessId}`);
    } catch (error) {
      console.error('Erro ao deletar acesso do usuário:', error);
      throw error;
    }
  }

  // Mestti OPS: acesso por usuário (padrão do nível + override individual)
  async getMyMesttiOpsAccess(): Promise<MyMesttiOpsAccess> {
    const response: AxiosResponse<MyMesttiOpsAccess> = await this.api.get('/users/me/mestti-ops-access');
    return response.data;
  }

  async getMesttiOpsAccess(userId: string): Promise<MesttiOpsAccess> {
    const response: AxiosResponse<MesttiOpsAccess> = await this.api.get(`/users/${userId}/mestti-ops-access`);
    return response.data;
  }

  /** `null` remove o override (volta ao padrão do nível). */
  async setMesttiOpsAccess(userId: string, enabled: boolean | null): Promise<MesttiOpsAccess> {
    const response: AxiosResponse<MesttiOpsAccess> = await this.api.put(
      `/users/${userId}/mestti-ops-access`,
      { enabled },
    );
    return response.data;
  }

  // Favoritos de Tabs
  async getFavoriteTabs(): Promise<{ favorites: FavoriteTabResponseItem[]; total: number }> {
    try {
      const response: AxiosResponse<{ favorites: FavoriteTabResponseItem[]; total: number }> =
        await this.api.get('/user-favorite-tabs');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar favoritos:', error);
      throw error;
    }
  }

  async createFavoriteTab(favorite: { path: string, label: string, order_index?: number }): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post('/user-favorite-tabs', favorite);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar favorito:', error);
      throw error;
    }
  }

  async updateFavoriteTab(favoriteId: string, update: { label?: string, order_index?: number }): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.put(`/user-favorite-tabs/${favoriteId}`, update);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar favorito:', error);
      throw error;
    }
  }

  async deleteFavoriteTab(favoriteId: string): Promise<void> {
    try {
      await this.api.delete(`/user-favorite-tabs/${favoriteId}`);
    } catch (error) {
      console.error('Erro ao deletar favorito:', error);
      throw error;
    }
  }

  async reorderFavoriteTabs(favoriteIds: string[]): Promise<{ favorites: any[], total: number }> {
    try {
      const response: AxiosResponse<{ favorites: any[], total: number }> = await this.api.post('/user-favorite-tabs/reorder', favoriteIds);
      return response.data;
    } catch (error) {
      console.error('Erro ao reordenar favoritos:', error);
      throw error;
    }
  }
}

export const apiService = new ApiService();
